/**
 * Generic LocalStorage Utility
 *
 * Provides type-safe, generic functions for storing and retrieving data from localStorage.
 * Can be used for any storage key (appSettings, githubSettings, etc.)
 */

export interface ProjectGitDetails {
    repo_url?: string;
    branch?: string;
    token?: string;
}

interface ProjectGitSettingsRecord {
    projectid: string;
    gitdetails: ProjectGitDetails;
}

type ProjectGitSettingsMap = Record<string, ProjectGitSettingsRecord>;

export const GITHUB_SETTINGS_STORAGE_KEY = 'githubSettings';
const GLOBAL_PROJECT_SCOPE_KEY = '__global__';

function normalizeProjectScope(projectId: string | number | null | undefined): string {
    if (projectId == null) return GLOBAL_PROJECT_SCOPE_KEY;
    const value = String(projectId).trim();
    return value.length > 0 ? value : GLOBAL_PROJECT_SCOPE_KEY;
}

/** Merge current and legacy field names from API or older localStorage rows. */
export function normalizeProjectGitDetails(d: ProjectGitDetails | null | undefined): ProjectGitDetails | null {
    if (!d || typeof d !== 'object') return null;
    const o = d as Record<string, unknown>;
    const repo_url = String(d.repo_url ?? o.repository_path ?? '').trim();
    const branchRaw = String(d.branch ?? o.branch_or_feature ?? '').trim();
    const token = String(d.token ?? o.github_token ?? '').trim();
    return {
        repo_url,
        branch: branchRaw.length > 0 ? branchRaw : 'main',
        token,
    };
}

function readGitSettingsMapFromStorage(): ProjectGitSettingsMap {
    const stored = getFromStorage<unknown>(GITHUB_SETTINGS_STORAGE_KEY);
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
        return {};
    }

    // Backward compatibility: older shape was a flat settings object.
    const flat = stored as Record<string, unknown>;
    if ('repo_url' in flat || 'branch' in flat || 'branch_or_feature' in flat || 'token' in flat) {
        const legacy = stored as ProjectGitDetails & Record<string, unknown>;
        const migrated: ProjectGitSettingsMap = {
            [GLOBAL_PROJECT_SCOPE_KEY]: {
                projectid: GLOBAL_PROJECT_SCOPE_KEY,
                gitdetails: normalizeProjectGitDetails({
                    repo_url: legacy.repo_url,
                    branch: (legacy.branch as string) ?? (legacy.branch_or_feature as string),
                    token: legacy.token,
                })!,
            },
        };
        writeGitSettingsMapToStorage(migrated);
        return migrated;
    }

    return stored as ProjectGitSettingsMap;
}

function writeGitSettingsMapToStorage(map: ProjectGitSettingsMap): void {
    saveToStorage(GITHUB_SETTINGS_STORAGE_KEY, map);
}

/**
 * Backward-compatible key helper. New storage is centralized under `githubSettings`.
 */
export function gitSettingsStorageKey(_projectId: string | number | null | undefined): string {
    return GITHUB_SETTINGS_STORAGE_KEY;
}

/**
 * Save Git settings under a project-scoped record.
 */
export function saveProjectGitSettingsToStorage(
    projectId: string | number | null | undefined,
    gitdetails: ProjectGitDetails
): void {
    const key = normalizeProjectScope(projectId);
    const map = readGitSettingsMapFromStorage();
    const n = normalizeProjectGitDetails(gitdetails) ?? { repo_url: '', branch: 'main', token: '' };
    map[key] = {
        projectid: key,
        gitdetails: {
            repo_url: n.repo_url ?? '',
            branch: n.branch ?? 'main',
            token: n.token ?? '',
        },
    };
    writeGitSettingsMapToStorage(map);
}

/**
 * Read Git settings for a specific project id, with optional global fallback.
 */
export function getProjectGitSettingsFromStorage(
    projectId: string | number | null | undefined,
    options?: { includeGlobalFallback?: boolean }
): ProjectGitDetails | null {
    const includeGlobalFallback = options?.includeGlobalFallback ?? true;
    const map = readGitSettingsMapFromStorage();
    const key = normalizeProjectScope(projectId);

    const direct = map[key]?.gitdetails;
    if (direct && typeof direct === 'object') {
        return normalizeProjectGitDetails(direct);
    }
    if (!includeGlobalFallback) {
        return null;
    }
    const fallback = map[GLOBAL_PROJECT_SCOPE_KEY]?.gitdetails;
    return fallback && typeof fallback === 'object' ? normalizeProjectGitDetails(fallback) : null;
}

/**
 * Get data from localStorage by key
 * @param key - The localStorage key
 * @returns The parsed data or null if not found or invalid
 */
export function getFromStorage<T>(key: string): T | null {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) {
            return null;
        }
        return JSON.parse(stored) as T;
    } catch (error) {
        console.error(`Failed to load data from localStorage key "${key}":`, error);
        return null;
    }
}

/**
 * Save data to localStorage by key
 * @param key - The localStorage key
 * @param data - The data to save (will be JSON stringified)
 */
export function saveToStorage<T>(key: string, data: T): void {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(`Failed to save data to localStorage key "${key}":`, error);
        throw error;
    }
}

/**
 * Remove data from localStorage by key
 * @param key - The localStorage key to remove
 */
export function removeFromStorage(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error(`Failed to remove data from localStorage key "${key}":`, error);
    }
}

/**
 * Check if a key exists in localStorage
 * @param key - The localStorage key to check
 * @returns true if the key exists, false otherwise
 */
export function hasStorageKey(key: string): boolean {
    return localStorage.getItem(key) !== null;
}

/**
 * Get a specific field from stored data
 * @param key - The localStorage key
 * @param field - The field name to retrieve
 * @returns The field value or null if not found
 */
export function getFieldFromStorage<T, K extends keyof T>(key: string, field: K): T[K] | null {
    const data = getFromStorage<T>(key);
    if (!data) {
        return null;
    }
    return data[field] || null;
}

/**
 * Update a specific field in stored data
 * @param key - The localStorage key
 * @param field - The field name to update
 * @param value - The new value for the field
 */
export function updateFieldInStorage<T, K extends keyof T>(key: string, field: K, value: T[K]): void {
    const data = getFromStorage<T>(key) || ({} as T);
    const updated = {
        ...data,
        [field]: value,
    };
    saveToStorage(key, updated);
}
